// ** react imports
import React from 'react';

// ** ui components imports
import NormalButton from '../../../common/Buttons/NormalButton';
import Badge from '../../../common/Badge';

// ** enums imports
import {
  BUTTON_THEMES,
  BADGE_THEMES,
  BADGE_SIZES,
} from '../../../../enums/common';

// ** context imports
import { useQuiz } from '../../../../contexts/QuizContext';

const SectionHeader = ({
  section,
  isEditing,
  onEdit,
  onSave = () => {},
  onCancel = () => {},
  isSaving,
}) => {
  const { errors } = useQuiz();

  // Derive metrics from visible questions and current answers
  const visibleQuestions = (section.questions || []).filter(q => !q.isHidden);
  const totalVisible = visibleQuestions.length;
  const isAnswered = q => {
    const v = q?.answer;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'string') return v.trim().length > 0;
    if (typeof v === 'number') return true;
    if (typeof v === 'boolean') return true; // false is a valid answer
    if (v && typeof v === 'object') return Object.keys(v).length > 0;
    return false;
  };
  const requiredVisible = visibleQuestions.filter(
    q => q.required && !q.optedOut
  ).length;
  const answeredVisible = visibleQuestions.filter(
    q => q.optedOut || isAnswered(q)
  ).length;
  const invalidVisible = visibleQuestions.filter(q => {
    if (errors && errors[q.questionId]) return true;
    if (q.required && !q.optedOut && !isAnswered(q)) return true;
    return false;
  }).length;

  return (
    <div className="flex items-start justify-between mb-6 z-10 backdrop-blur-sm bg-white/50 dark:bg-gray-800/50">
      <div className="flex-1">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
          {section.title}
        </h3>
        {section.description && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {section.description}
          </p>
        )}
        <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
          <span>
            Visible: {totalVisible}{' '}
            {totalVisible === 1 ? 'question' : 'questions'}
          </span>
          <span>•</span>
          <span>Answered: {answeredVisible}</span>
          {requiredVisible > 0 && (
            <>
              <span>•</span>
              <span>Required: {requiredVisible}</span>
            </>
          )}
          {invalidVisible > 0 && (
            <>
              <span>•</span>
              <span className="text-red-500 dark:text-red-400">
                Invalid: {invalidVisible}
              </span>
            </>
          )}
        </div>
      </div>
      <div className="flex flex-col items-end space-y-2">
        {/* Edit/Save button */}
        <div className="flex items-center gap-2">
          {isEditing && (
            <NormalButton
              disabled={isSaving}
              onClick={onCancel}
              theme={BUTTON_THEMES.DANGER}
            >
              Cancel
            </NormalButton>
          )}
          <NormalButton
            disabled={isSaving}
            onClick={isEditing ? onSave : onEdit}
            theme={!isEditing ? BUTTON_THEMES.PRIMARY : BUTTON_THEMES.SUCCESS}
          >
            {isSaving ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Saving...
              </>
            ) : isEditing ? (
              <>
                <svg
                  className="w-4 h-4 mr-1 inline-block"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Save section
              </>
            ) : (
              <>
                <svg
                  className="w-4 h-4 mr-1 inline-block"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                Edit section
              </>
            )}
          </NormalButton>
          {section.metadata?.isOptional && (
            <Badge theme={BADGE_THEMES.INFO} size={BADGE_SIZES.SM}>
              Optional
            </Badge>
          )}
        </div>

        {/* Last saved indicator */}
        {section.lastSavedAt && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Saved: {new Date(section.lastSavedAt).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
};

export default SectionHeader;
